import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
  Tune,
} from '@mui/icons-material';
import { addDays, format } from 'date-fns';
import { shopTaskService } from '../services/shopTaskService';
import { employeeService } from '../services/employeeService';
import { formatTimeEntry, formatLocalTodayYmd, parseLocalDate } from '../utils/dateUtils';

const fmtClock = (v) => (v ? formatTimeEntry(v, 'h:mm a') : '—');
const round2 = (n) => Math.round(Number(n) * 100) / 100;

function rowsFromEntry(entry) {
  return (entry.task_allocations || []).map((a) => ({
    key: `a-${a.id}`,
    id: a.id,
    shop_task_id: a.shop_task_id,
    work_function_id: a.work_function_id ?? null,
    saved_task_id: a.shop_task_id,
    saved_function: a.work_function_id != null ? { id: a.work_function_id, label: a.function_label } : null,
    hours: String(a.hours),
    source: a.source,
    started_at: a.started_at,
    ended_at: a.ended_at,
  }));
}

const comparable = (rows) =>
  JSON.stringify(rows.map(({ id, shop_task_id, work_function_id, hours }) => ({
    id,
    shop_task_id,
    work_function_id: work_function_id ?? null,
    hours: String(hours),
  })));

/** Functions a row can pick: the task's functions, plus the row's saved one if it was since removed. */
function rowFunctionOptions(row, task) {
  const list = [...(task?.functions || [])];
  const saved = row.saved_function;
  if (saved && row.shop_task_id === row.saved_task_id && !list.some((f) => f.id === saved.id)) list.push(saved);
  return list;
}

function PunchAllocationEditor({ entry, taskOptions, onSaved }) {
  const [rows, setRows] = useState(() => rowsFromEntry(entry));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRows(rowsFromEntry(entry));
  }, [entry]);

  const isOpen = !entry.clock_out;
  const paid = Number(entry.hours_worked) || 0;
  const allocated = rows.reduce((s, r) => s + (parseFloat(r.hours) > 0 ? parseFloat(r.hours) : 0), 0);
  const remainder = round2(paid - allocated);
  const overAllocated = remainder < -0.005;
  const dirty = comparable(rowsFromEntry(entry)) !== comparable(rows);

  const optionById = useMemo(() => new Map(taskOptions.map((t) => [t.id, t])), [taskOptions]);

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const changeTask = (row, task) => {
    const fns = task?.functions || [];
    updateRow(row.key, {
      shop_task_id: task ? task.id : null,
      work_function_id: fns.length === 1 ? fns[0].id : null,
    });
  };

  const handleSave = async () => {
    if (rows.some((r) => !r.shop_task_id || !(parseFloat(r.hours) > 0))) {
      setError('Each row needs a task and hours greater than zero.');
      return;
    }
    if (rows.some((r) => rowFunctionOptions(r, optionById.get(r.shop_task_id)).length > 1 && r.work_function_id == null)) {
      setError('Pick a function for each task that has more than one.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await shopTaskService.saveAllocations(
        entry.id,
        rows.map((r) => ({
          id: r.id || undefined,
          shop_task_id: r.shop_task_id,
          work_function_id: r.work_function_id ?? undefined,
          hours: parseFloat(r.hours),
        }))
      );
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ border: 1, borderColor: entry.over_allocated ? 'error.main' : 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {fmtClock(entry.clock_in)} – {isOpen ? 'still clocked in' : fmtClock(entry.clock_out)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isOpen ? '' : `${paid.toFixed(2)} h paid`}
        </Typography>
        {entry.has_admin_allocations && <Chip size="small" label="Adjusted" color="info" variant="outlined" />}
        {entry.over_allocated && <Chip size="small" label="Over-allocated — fix" color="error" />}
      </Stack>

      {isOpen ? (
        <Typography variant="body2" color="text.secondary">
          Now on <strong>{entry.current_task_label}</strong>. Hours can be reallocated after they clock out.
          {entry.task_summary ? ` So far: ${entry.task_summary}` : ''}
        </Typography>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell width={170}>Function</TableCell>
                <TableCell width={110}>Hours</TableCell>
                <TableCell width={170}>Source</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={taskOptions}
                      value={optionById.get(r.shop_task_id) || null}
                      getOptionLabel={(o) => o.label || ''}
                      groupBy={(o) => (o.status === 'closed' ? 'Closed tasks' : 'Open tasks')}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      onChange={(_e, v) => changeTask(r, v)}
                      renderInput={(params) => <TextField {...params} placeholder="Pick a task" />}
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const fns = rowFunctionOptions(r, optionById.get(r.shop_task_id));
                      if (fns.length <= 1) {
                        return (
                          <Typography variant="body2" color="text.secondary">{fns[0]?.label || '—'}</Typography>
                        );
                      }
                      return (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={r.work_function_id ?? ''}
                          onChange={(e) => updateRow(r.key, { work_function_id: e.target.value === '' ? null : Number(e.target.value) })}
                          SelectProps={{ displayEmpty: true }}
                        >
                          <MenuItem value="" disabled>Pick…</MenuItem>
                          {fns.map((f) => (
                            <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>
                          ))}
                        </TextField>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={r.hours}
                      inputProps={{ min: 0.25, step: 0.25 }}
                      onChange={(e) => updateRow(r.key, { hours: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {!r.id
                        ? 'New'
                        : r.source === 'kiosk' && r.started_at
                          ? `Kiosk ${fmtClock(r.started_at)}–${fmtClock(r.ended_at)}`
                          : 'Manager adjusted'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" aria-label="Remove row" onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} sx={{ color: 'text.secondary' }}>{entry.default_task_label} (remainder)</TableCell>
                <TableCell sx={{ color: overAllocated ? 'error.main' : 'text.secondary', fontWeight: 600 }}>
                  {remainder.toFixed(2)}
                </TableCell>
                <TableCell colSpan={2}>
                  <Typography variant="caption" color="text.secondary">Automatic</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
          {overAllocated && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Allocated hours exceed the {paid.toFixed(2)} paid hours on this punch.
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setRows((prev) => [
                  ...prev,
                  {
                    key: `n-${Date.now()}-${Math.random()}`,
                    id: null,
                    shop_task_id: null,
                    work_function_id: null,
                    hours: remainder > 0 ? String(remainder) : '',
                  },
                ])
              }
            >
              Add task
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {dirty && (
              <Button size="small" onClick={() => setRows(rowsFromEntry(entry))} disabled={saving}>
                Reset
              </Button>
            )}
            <Button size="small" variant="contained" onClick={handleSave} disabled={!dirty || saving || overAllocated}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

const LaborAllocation = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState(searchParams.get('date') || formatLocalTodayYmd());
  const [employeeId, setEmployeeId] = useState(searchParams.get('employee') ? parseInt(searchParams.get('employee'), 10) : null);
  const [employees, setEmployees] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [empRes, taskRes] = await Promise.all([
          employeeService.getAll({ activeOnly: 'true', limit: 500 }),
          shopTaskService.getAll({ limit: 500 }),
        ]);
        setEmployees(empRes.data ?? []);
        setTaskOptions((taskRes.data || []).filter((t) => !t.is_default));
      } catch {
        /* lookups optional */
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await shopTaskService.getAllocations({ date, employeeId });
      setEntries(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load punches');
    } finally {
      setLoading(false);
    }
  }, [date, employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const next = { date };
    if (employeeId) next.employee = String(employeeId);
    setSearchParams(next, { replace: true });
  }, [date, employeeId, setSearchParams]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!map.has(e.employee_id)) {
        map.set(e.employee_id, {
          employeeId: e.employee_id,
          name: [e.employee_first_name, e.employee_last_name].filter(Boolean).join(' ') || `#${e.employee_id}`,
          entries: [],
        });
      }
      map.get(e.employee_id).entries.push(e);
    }
    return [...map.values()];
  }, [entries]);

  const shiftDate = (days) => {
    const d = parseLocalDate(date) || new Date();
    setDate(format(addDays(d, days), 'yyyy-MM-dd'));
  };

  const handleSaved = (updated) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
  };

  const selectedEmployee = employees.find((e) => e.id === employeeId) || null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <Tune color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Labor Allocation
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/shop-tasks')}>Tasks</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Move hours from a punch onto the tasks the employee actually worked. Paid hours never change; anything not
        allocated stays on the default task.
      </Typography>

      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <IconButton aria-label="Previous day" onClick={() => shiftDate(-1)}><ChevronLeft /></IconButton>
        <TextField
          type="date"
          size="small"
          label="Date"
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <IconButton aria-label="Next day" onClick={() => shiftDate(1)}><ChevronRight /></IconButton>
        <Autocomplete
          size="small"
          options={employees}
          value={selectedEmployee}
          getOptionLabel={(e) => [e.first_name, e.last_name].filter(Boolean).join(' ')}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_e, v) => setEmployeeId(v ? v.id : null)}
          renderInput={(params) => <TextField {...params} label="Employee" placeholder="All employees" />}
          sx={{ minWidth: 240 }}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : groups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No punches on this day.</Paper>
      ) : (
        <Stack spacing={2}>
          {groups.map((g) => {
            const paid = g.entries.reduce((s, e) => s + (Number(e.hours_worked) || 0), 0);
            return (
              <Paper key={g.employeeId} sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{g.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paid.toFixed(2)} h paid · {g.entries.length} punch{g.entries.length === 1 ? '' : 'es'}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {g.entries.map((e) => (
                    <PunchAllocationEditor key={e.id} entry={e} taskOptions={taskOptions} onSaved={handleSaved} />
                  ))}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default LaborAllocation;
